import { useState } from "react";
import { supabase } from "@/lib/supabase";

interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  credit: number;
}

interface CreditAdjustModalProps {
  user: UserProfile;
  onClose: () => void;
  onSuccess: (newCredit: number) => void;
}

const PRESET_AMOUNTS = [10000, 30000, 50000, 100000, 300000, 500000];

const QUICK_REASONS: Record<"grant" | "deduct", string[]> = {
  grant: ["이벤트 보상", "수동지급", "오류 보상", "VIP 혜택", "직접 입력"],
  deduct: ["어뷰징 제재", "환불 처리", "수동 차감", "직접 입력"],
};

export default function CreditAdjustModal({ user, onClose, onSuccess }: CreditAdjustModalProps) {
  const [adjustType, setAdjustType] = useState<"grant" | "deduct">("grant");
  const [amount, setAmount] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const displayName = user.full_name || user.email || "이름 없음";
  const numAmount = Number(amount.replace(/,/g, ""));
  const isCustomReason = selectedReason === "직접 입력";
  const finalReason = isCustomReason ? customReason : selectedReason;

  const previewCredit = (() => {
    if (!numAmount || numAmount <= 0) return user.credit;
    return adjustType === "grant" ? user.credit + numAmount : user.credit - numAmount;
  })();

  const isValid =
    numAmount > 0 &&
    finalReason.trim().length > 0 &&
    (adjustType === "grant" || previewCredit >= 0);

  const handleAmountInput = (val: string) => {
    const numeric = val.replace(/[^0-9]/g, "");
    setAmount(numeric ? Number(numeric).toLocaleString() : "");
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token ?? "";
      const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

      const res = await fetch(`${supabaseUrl}/functions/v1/admin-credit-adjust`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": anonKey,
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetUserId: user.id,
          adjustType,
          amount: numAmount,
          description: finalReason.trim(),
        }),
      });

      const json = await res.json();

      if (!res.ok || json.error) {
        if (json.error === "Insufficient credit") {
          setError(`잔여 크레딧이 부족합니다. (현재: ₩${(json.currentCredit ?? user.credit).toLocaleString()})`);
        } else {
          setError(json.error || "처리 중 오류가 발생했습니다.");
        }
      } else {
        setSuccess(true);
        setTimeout(() => {
          onSuccess(json.newCredit);
          onClose();
        }, 1200);
      }
    } catch (e) {
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#13131f] border border-white/[0.1] rounded-2xl w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white text-sm font-bold shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-white font-semibold text-sm leading-tight">{displayName}</p>
              <p className="text-gray-500 text-xs mt-0.5">크레딧 조정</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* 현재 크레딧 */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-coin-line text-gray-500 text-sm"></i>
              <span className="text-gray-400 text-sm">현재 잔여 크레딧</span>
            </div>
            <span className="text-white font-bold text-base">₩{(user.credit || 0).toLocaleString()}</span>
          </div>

          {/* 지급 / 차감 토글 */}
          <div className="flex rounded-xl overflow-hidden border border-white/[0.08] p-1 gap-1">
            {(["grant", "deduct"] as const).map((type) => (
              <button
                key={type}
                onClick={() => { setAdjustType(type); setSelectedReason(""); setError(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  adjustType === type
                    ? type === "grant"
                      ? "bg-emerald-600 text-white"
                      : "bg-red-600 text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                <i className={type === "grant" ? "ri-add-circle-line" : "ri-indeterminate-circle-line"}></i>
                {type === "grant" ? "크레딧 지급" : "크레딧 차감"}
              </button>
            ))}
          </div>

          {/* 금액 입력 */}
          <div>
            <label className="text-gray-400 text-xs font-medium mb-2 block">금액 (원)</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">₩</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => handleAmountInput(e.target.value)}
                placeholder="0"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-8 pr-4 py-3 text-white text-base font-semibold focus:outline-none focus:border-rose-500/50 placeholder-gray-700"
              />
            </div>
            {/* 프리셋 금액 */}
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESET_AMOUNTS.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setAmount(preset.toLocaleString())}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                    numAmount === preset
                      ? "bg-rose-600/30 border-rose-500/50 text-rose-300"
                      : "bg-white/[0.04] border-white/[0.06] text-gray-400 hover:text-white hover:border-white/[0.15]"
                  }`}
                >
                  ₩{preset.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* 사유 선택 */}
          <div>
            <label className="text-gray-400 text-xs font-medium mb-2 block">사유 선택</label>
            <div className="flex flex-wrap gap-2">
              {QUICK_REASONS[adjustType].map((reason) => (
                <button
                  key={reason}
                  onClick={() => { setSelectedReason(reason); if (reason !== "직접 입력") setCustomReason(""); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                    selectedReason === reason
                      ? "bg-rose-600/30 border-rose-500/50 text-rose-300"
                      : "bg-white/[0.04] border-white/[0.06] text-gray-400 hover:text-white hover:border-white/[0.15]"
                  }`}
                >
                  {reason}
                </button>
              ))}
            </div>
            {isCustomReason && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="사유를 직접 입력하세요"
                maxLength={100}
                className="mt-2 w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-rose-500/50 placeholder-gray-700"
              />
            )}
          </div>

          {/* 변경 후 미리보기 */}
          {numAmount > 0 && (
            <div className={`rounded-xl px-4 py-3 border flex items-center justify-between ${
              adjustType === "grant"
                ? "bg-emerald-500/5 border-emerald-500/20"
                : previewCredit < 0
                  ? "bg-red-500/10 border-red-500/30"
                  : "bg-red-500/5 border-red-500/20"
            }`}>
              <div className="flex items-center gap-2">
                <i className={`text-sm ${adjustType === "grant" ? "ri-arrow-up-circle-line text-emerald-400" : "ri-arrow-down-circle-line text-red-400"}`}></i>
                <span className="text-gray-400 text-sm">조정 후 크레딧</span>
              </div>
              <div className="text-right">
                <span className={`font-bold text-base ${previewCredit < 0 ? "text-red-400" : "text-white"}`}>
                  ₩{Math.max(0, previewCredit).toLocaleString()}
                </span>
                <span className={`ml-2 text-xs font-medium ${adjustType === "grant" ? "text-emerald-400" : "text-red-400"}`}>
                  {adjustType === "grant" ? "+" : "-"}₩{numAmount.toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* 에러 */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/25 rounded-xl">
              <i className="ri-error-warning-line text-red-400 text-sm shrink-0"></i>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* 성공 */}
          {success && (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/25 rounded-xl">
              <i className="ri-checkbox-circle-line text-emerald-400 text-sm shrink-0"></i>
              <p className="text-emerald-400 text-sm">크레딧이 성공적으로 {adjustType === "grant" ? "지급" : "차감"}되었습니다!</p>
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid || loading || success}
              className={`flex-1 py-3 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                adjustType === "grant"
                  ? "bg-emerald-600 hover:bg-emerald-500"
                  : "bg-red-600 hover:bg-red-500"
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  처리 중...
                </span>
              ) : success ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-checkbox-circle-line"></i>완료
                </span>
              ) : (
                `${adjustType === "grant" ? "지급" : "차감"} 확인`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}